"use client";

import { useCallback, useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { HttpError } from "@utilities/api";
import { logger } from "@utilities/logger";
import {
  InteractiveImage,
  type InteractiveImageGenerate,
  type InteractiveImageGenerationError,
  type InteractiveImageProps,
} from "@ui/components/demos/InteractiveImage";

import { buildOnGenerate } from "./buildOnGenerate";
import { mapGenerationError } from "./mapGenerationError";

/** Cap the logged prompt so unbounded free-text input can't bloat PostHog properties. */
const PROMPT_LOG_MAX_LENGTH = 500;

interface InteractiveImageLiveProps extends Omit<
  InteractiveImageProps,
  "onGenerate" | "generationError" | "onGenerationErrorClear" | "creationResults"
> {
  /** Legacy template slug for a first generation (text → image). */
  creationTemplateSlug: string;
  /** Legacy template slug for an edit (image + text → image). */
  editionTemplateSlug: string;
}

/**
 * Live-generation wrapper around the pure {@link InteractiveImage}. Owns the network wiring and
 * error state so `_ui` stays backend-agnostic; quota/rate-limit failures surface the sign-up CTA,
 * any other failure an inline retry.
 */
export function InteractiveImageLive({
  creationTemplateSlug,
  editionTemplateSlug,
  ...rest
}: InteractiveImageLiveProps) {
  const t = useTranslations("ClientKeys.InteractiveImage");
  const [generationError, setGenerationError] = useState<InteractiveImageGenerationError | null>(null);

  const generate = useMemo(
    () => buildOnGenerate({ creationTemplateSlug, editionTemplateSlug }),
    [creationTemplateSlug, editionTemplateSlug],
  );

  const onGenerate = useCallback<InteractiveImageGenerate>(
    async input => {
      setGenerationError(null);
      // Same anonymous creative-templates flow as the Hero Omnibox, so it reuses the omnibox events;
      // `surface` lets omnibox dashboards filter this demo out (`where surface = omnibox`).
      const prompt = input.prompt.trim().slice(0, PROMPT_LOG_MAX_LENGTH);
      const eventProps = {
        surface: "interactive_image",
        input_type: input.attachments.length > 0 ? "file" : "text",
        prompt,
      };

      captureEvent(EventName.mw_creative_template_omnibox_generation_started, eventProps);

      try {
        const result = await generate(input);
        captureEvent(EventName.mw_creative_template_omnibox_generation_succeeded, eventProps);
        return result;
      } catch (err) {
        // Aborted = silent cancel; let the component's request-id guard drop it (no terminal event).
        if (input.signal.aborted) throw err;
        const mapped = mapGenerationError(err, { gated: t("error_quota"), retry: t("error_generic") });
        const errorType = err instanceof HttpError ? err.errorType : null;
        if (mapped.kind === "gated") {
          captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, { ...eventProps, error_type: errorType });
        } else {
          captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
            ...eventProps,
            error_type: errorType,
          });
          // Only log non-gated failures; gated ones are normal quota flow.
          logger.error(err instanceof Error ? err : null, "[InteractiveImageLive] generation failed");
        }
        setGenerationError(mapped);
        throw err;
      }
    },
    [generate, t],
  );

  return (
    <InteractiveImage
      {...rest}
      onGenerate={onGenerate}
      generationError={generationError ?? undefined}
      onGenerationErrorClear={() => setGenerationError(null)}
    />
  );
}
